import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import type { TaskView } from '@/lib/api/generated/model';
import { MOVE_FAILED, clientBusyRefusal } from '../errors';
import { isBusy, waitingOnChildren } from '../executionStatus';
import { STOP_WAITING_NOTE } from '../linkedWaiting';
import type { Outcome, StepRef } from '../primaryAction';
import {
  messageOf,
  sayOutcome,
  type CommandOptions,
  type CommandResult,
} from './commandOutcome';
import { useOpenLinkedAccounts } from '@/features/identity';
import { refusalToastOptions } from '../moveRefusalToast';
import { useMoveTask } from './useMoveTask';
import {
  isPersonalAccountRefused,
  useRerunTask,
  useStopTask,
} from './useTaskLifecycle';

/**
 * The one place an action on a task is fired and its outcome said (spec 125
 * FR-009, research R9). Every surface — drawer header, card, card menu,
 * Overview row, palette, shortcut — calls these, so a Stop reads the same
 * wherever it was pressed and none of them can forget to confirm it.
 *
 * Refusals keep the words they arrive with: the service's, or `errors.ts`'s.
 *
 * Outcomes are said through `sayOutcome`, from the promise `mutateAsync`
 * returns, never from callbacks handed to `mutate` (spec 151 FR-001): those
 * are dropped when the calling row or card unmounts, which an optimistic move
 * always causes, and a refused Done on the Overview used to roll back
 * without a word.
 *
 * `report` has the same commands resolving with how each ended (spec 154
 * R2), after its toast, so a caller can keep a refusal's words. Every command
 * takes `keepalive` for an action sent as the page goes away (R3).
 */
export function useTaskCommands(workspaceId: string) {
  const move = useMoveTask();
  const stopTask = useStopTask();
  const rerunTask = useRerunTask();
  const openLinkedAccounts = useOpenLinkedAccounts();

  const moveTo = useCallback(
    (
      task: TaskView,
      targetStepId: string,
      success: string,
      onDone?: () => void,
      options: CommandOptions = {},
    ): Promise<CommandResult> => {
      // Refused before the request, exactly as a busy drag is, and in the same
      // words — one wording for one refusal.
      if (isBusy(task)) {
        const { message } = clientBusyRefusal();
        toast.error(message);
        return Promise.resolve({ ok: false, message });
      }
      return new Promise<CommandResult>((resolve) => {
        sayOutcome(
          move.mutateAsync({
            workspaceId,
            taskId: task.id,
            targetStepId,
            expectedVersion: task.version,
            ...options,
          }),
          {
            accepted: (result) => {
              if (result.refusal) {
                toast.error(
                  result.refusal.message,
                  refusalToastOptions(result.refusal, openLinkedAccounts),
                );
                resolve({ ok: false, message: result.refusal.message });
                return;
              }
              toast.success(success);
              resolve({ ok: true });
              onDone?.();
            },
            failed: () => {
              toast.error(MOVE_FAILED);
              resolve({ ok: false, message: MOVE_FAILED });
            },
          },
        );
      });
    },
    [move, openLinkedAccounts, workspaceId],
  );

  const start = useCallback(
    (
      task: TaskView,
      target: StepRef,
      onDone?: () => void,
      options?: CommandOptions,
    ) =>
      moveTo(
        task,
        target.stepId,
        `Started ${task.title} at ${target.label}`,
        onDone,
        options,
      ),
    [moveTo],
  );

  const decide = useCallback(
    (
      task: TaskView,
      outcome: Outcome,
      targetLabel: string,
      onDone?: () => void,
      options?: CommandOptions,
    ) =>
      moveTo(
        task,
        outcome.to,
        `Moved ${task.title} to ${targetLabel}`,
        onDone,
        options,
      ),
    [moveTo],
  );

  const stop = useCallback(
    (
      task: Pick<TaskView, 'id' | 'title'> &
        Partial<Pick<TaskView, 'executions' | 'linked_tasks'>>,
      options: { waitingOnChildren?: boolean } = {},
    ) => {
      // Stopping a parent that waits on its children stops the waiting, not
      // them (spec 097 FR-012) — said, so nobody goes looking for them.
      const waiting =
        options.waitingOnChildren ?? waitingOnChildren(task) !== null;
      sayOutcome(stopTask.mutateAsync({ workspaceId, taskId: task.id }), {
        accepted: () => {
          toast.success(
            `Stopped ${task.title}`,
            waiting ? { description: STOP_WAITING_NOTE } : undefined,
          );
        },
        failed: (error) => {
          toast.error(`Could not stop ${task.title}: ${messageOf(error)}`);
        },
      });
    },
    [stopTask, workspaceId],
  );

  const runAgain = useCallback(
    (
      task: Pick<TaskView, 'id' | 'title'>,
      options: CommandOptions = {},
    ): Promise<CommandResult> =>
      new Promise<CommandResult>((resolve) => {
        sayOutcome(
          rerunTask.mutateAsync({ workspaceId, taskId: task.id, ...options }),
          {
            accepted: () => {
              toast.success(`Running ${task.title} again`);
              resolve({ ok: true });
            },
            failed: (error) => {
              // The workspace requires the person's own account (spec 160): the
              // service's sentence says it all, with the way to link.
              if (isPersonalAccountRefused(error)) {
                toast.error(
                  error.message,
                  refusalToastOptions(
                    { kind: 'needs-account', hosts: error.hosts },
                    openLinkedAccounts,
                  ),
                );
                resolve({ ok: false, message: error.message });
                return;
              }
              const message = `Could not run ${task.title} again: ${messageOf(error)}`;
              toast.error(message);
              resolve({ ok: false, message });
            },
          },
        );
      }),
    [openLinkedAccounts, rerunTask, workspaceId],
  );

  // Fire and forget for every surface that acts at once, as before; `report`
  // resolves with how each ended, for the Overview's held actions.
  return useMemo(
    () => ({
      start: (...args: Parameters<typeof start>): void => {
        void start(...args);
      },
      decide: (...args: Parameters<typeof decide>): void => {
        void decide(...args);
      },
      stop,
      runAgain: (...args: Parameters<typeof runAgain>): void => {
        void runAgain(...args);
      },
      report: { start, decide, runAgain },
    }),
    [start, decide, stop, runAgain],
  );
}
